/**
 * E06-F03-T13-S03 — the website line of a Review row.
 *
 * Known website → the domain link (plus a subtle "entered" mark when the user typed
 * it). Unknown → "Website not found" (never "no website" — the company has a site,
 * we just did not find it) with an inline input to enter it; saved via
 * PATCH …/review/:rowId/website, errors (400/409/404) shown inline.
 *
 * E06-F03-T14 — a reference company's website found by code is marked "Domain not
 * confirmed" until a person presses "Confirm domain" (POST …/website/confirm) or
 * types another one ("Change" → the same input). A confirmed one is marked
 * "Domain confirmed".
 */
import { useState, type FormEvent, type JSX } from 'react';
import {
  WEBSITE_CONFIRMED_LABEL,
  WEBSITE_NOT_FOUND_LABEL,
  WEBSITE_UNCONFIRMED_LABEL,
  type ReviewRow,
} from '@eer/shared';
import { CompanyDomain } from './CompanyLabel.js';
import { useConfirmReviewWebsite, useSetReviewWebsite } from './researchReviewApi.js';
import {
  WEBSITE_MAX_LENGTH,
  isWebsiteNotFound,
  rowWebsite,
  validateWebsiteInput,
  websiteSaveErrorMessage,
} from './referenceSiteModel.js';

/** The inline "type the website" form (used for "not found" and for "Change"). */
function WebsiteForm({
  requestId,
  rowId,
  initial,
  onDone,
}: {
  requestId: number;
  rowId: number;
  initial: string;
  onDone: () => void;
}): JSX.Element {
  const save = useSetReviewWebsite(requestId);
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const checked = validateWebsiteInput(value);
    if (!checked.ok) {
      setError(checked.message);
      return;
    }
    setError(null);
    save.mutate(
      { rowId, website: checked.value },
      {
        onSuccess: () => onDone(),
        onError: (err) => setError(websiteSaveErrorMessage(err.detail)),
      },
    );
  }

  return (
    <>
      <form className="mt-1 flex items-center gap-1" onSubmit={handleSubmit} noValidate>
        <input
          type="text"
          data-testid="review-website-input"
          aria-label="Company website"
          placeholder="acme.com"
          autoFocus
          maxLength={WEBSITE_MAX_LENGTH}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={save.isPending}
          className="w-40 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-slate-800"
        />
        <button
          type="submit"
          data-testid="review-website-save"
          disabled={save.isPending}
          className="rounded bg-slate-900 px-2 py-0.5 text-[11px] text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          data-testid="review-website-cancel"
          disabled={save.isPending}
          onClick={onDone}
          className="text-[11px] text-slate-500 hover:text-slate-800"
        >
          Cancel
        </button>
      </form>
      {error ? (
        <p
          data-testid="review-website-error"
          role="alert"
          className="mt-0.5 text-[11px] text-red-600"
        >
          {error}
        </p>
      ) : null}
    </>
  );
}

/** E06-F03-T14 — confirmation mark + "Confirm domain" / "Change" of a reference website. */
function ReferenceWebsiteActions({
  requestId,
  row,
  onChange,
}: {
  requestId: number;
  row: ReviewRow;
  onChange: () => void;
}): JSX.Element | null {
  const confirm = useConfirmReviewWebsite(requestId);
  const [error, setError] = useState<string | null>(null);
  const state = row.websiteConfirmation ?? null;
  if (state === null) return null;

  function handleConfirm(): void {
    setError(null);
    confirm.mutate(
      { rowId: row.id },
      { onError: (err) => setError(websiteSaveErrorMessage(err.detail)) },
    );
  }

  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
      {state === 'unconfirmed' ? (
        <>
          <span
            data-testid={`review-website-unconfirmed-${row.id}`}
            data-website-confirmation="unconfirmed"
            title="Found automatically — check that this is the right company before the AI check"
            className="rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700"
          >
            <i className="fa-solid fa-triangle-exclamation mr-0.5" aria-hidden />
            {WEBSITE_UNCONFIRMED_LABEL}
          </span>
          <button
            type="button"
            data-testid="review-website-confirm"
            disabled={confirm.isPending}
            onClick={handleConfirm}
            className="text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-50"
          >
            <i className="fa-solid fa-check mr-0.5" aria-hidden />
            {confirm.isPending ? 'Confirming…' : 'Confirm domain'}
          </button>
        </>
      ) : (
        <span
          data-testid={`review-website-confirmed-${row.id}`}
          data-website-confirmation="confirmed"
          className="rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-700"
        >
          <i className="fa-solid fa-circle-check mr-0.5" aria-hidden />
          {WEBSITE_CONFIRMED_LABEL}
        </span>
      )}
      <button
        type="button"
        data-testid="review-website-change"
        disabled={confirm.isPending}
        onClick={onChange}
        className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
      >
        <i className="fa-solid fa-pen mr-0.5" aria-hidden /> Change
      </button>
      {error ? (
        <p
          data-testid="review-website-error"
          role="alert"
          className="w-full text-[11px] text-red-600"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function ReviewWebsiteCell({
  requestId,
  row,
}: {
  requestId: number;
  row: ReviewRow;
}): JSX.Element {
  const [editing, setEditing] = useState(false);

  if (!isWebsiteNotFound(row)) {
    return (
      <div data-testid={`review-website-${row.id}`}>
        <CompanyDomain domain={rowWebsite(row)} />
        {row.websiteSource === 'manual' ? (
          <span
            className="ml-1 text-[9px] text-slate-400"
            data-testid={`review-website-manual-${row.id}`}
            title="Website entered by hand"
          >
            · entered
          </span>
        ) : null}
        {editing ? (
          <WebsiteForm
            requestId={requestId}
            rowId={row.id}
            initial={rowWebsite(row) ?? ''}
            onDone={() => setEditing(false)}
          />
        ) : (
          <ReferenceWebsiteActions
            requestId={requestId}
            row={row}
            onChange={() => setEditing(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div data-testid="review-website-not-found" data-row-id={row.id}>
      <span className="text-[11px] italic text-amber-600">{WEBSITE_NOT_FOUND_LABEL}</span>
      {!editing ? (
        <button
          type="button"
          data-testid="review-website-edit"
          onClick={() => setEditing(true)}
          className="ml-1.5 text-[11px] text-blue-600 hover:underline"
        >
          <i className="fa-solid fa-pen mr-0.5" aria-hidden /> Enter website
        </button>
      ) : (
        <WebsiteForm
          requestId={requestId}
          rowId={row.id}
          initial=""
          onDone={() => setEditing(false)}
        />
      )}
    </div>
  );
}
